import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, addDays, parseISO, isSameDay } from 'date-fns';
import api from '../config/api';

/**
 * FlightPriceGrid Component
 * Displays a grid of flight prices for dates around the selected departure/return dates
 * Similar to Google Flights price calendar
 */
function FlightPriceGrid({ 
  origin, 
  destination, 
  departDate, 
  returnDate, 
  passengers, 
  tripType,
  selectionStep,
  onDateSelect 
}) {
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [cheapestPrice, setCheapestPrice] = useState(null);
  const [mostExpensivePrice, setMostExpensivePrice] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [rateLimitEnd, setRateLimitEnd] = useState(null);

  useEffect(() => {
    if (origin && destination && departDate && !rateLimited) {
      fetchPriceGrid();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, departDate, returnDate, passengers, tripType, weekOffset]);
  
  // Check rate limit timer
  useEffect(() => {
    if (rateLimited && rateLimitEnd) {
      const interval = setInterval(() => {
        if (Date.now() >= rateLimitEnd) {
          setRateLimited(false);
          setRateLimitEnd(null);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [rateLimited, rateLimitEnd]);

  const fetchPriceGrid = async () => {
    setLoading(true);
    try {
      // Validate date format
      if (!departDate || typeof departDate !== 'string') {
        console.error('Invalid departDate:', departDate);
        setLoading(false);
        return;
      }

      const baseDate = parseISO(departDate);
      if (isNaN(baseDate.getTime())) {
        console.error('Invalid date format:', departDate);
        setLoading(false);
        return;
      }

      const offsetDate = addDays(baseDate, weekOffset * 7);
      
      // Fetch prices for a 7-day window
      const pricePromises = [];
      
      for (let i = -3; i <= 3; i++) {
        const date = addDays(offsetDate, i);
        
        const params = {
          origin,
          destination,
          departure_date: format(date, 'yyyy-MM-dd'),
          passengers: parseInt(passengers) || 1,
          cabin_class: 'economy'
        };
        
        if (returnDate && tripType === 'round_trip') {
          // Keep same trip duration
          const returnDateObj = parseISO(returnDate);
          if (!isNaN(returnDateObj.getTime())) {
            const duration = Math.round((returnDateObj - baseDate) / (1000 * 60 * 60 * 24));
            params.return_date = format(addDays(date, duration), 'yyyy-MM-dd');
          }
        }
        
        console.log(`📅 Fetching price for ${format(date, 'yyyy-MM-dd')}:`, params);
        
        pricePromises.push(
          api.post('/duffel/search', params)
            .then(response => {
              const firstFlight = response.data.flights?.[0];
              
              // Extract numeric price from total_amount
              const price = firstFlight?.total_amount ? parseFloat(firstFlight.total_amount) : null;
              const currency = firstFlight?.total_currency || 'USD';
              
              console.log(`💰 Price for ${format(date, 'yyyy-MM-dd')}: ${currency} ${price}`);
              
              return {
                date,
                price,
                currency
              };
            })
            .catch(err => {
              const errorData = err.response?.data;
              console.error(`❌ Price fetch error for ${format(date, 'yyyy-MM-dd')}:`, errorData || err.message);
              
              // Check if rate limited
              if (errorData?.message?.includes('Rate limited')) {
                console.warn('⏸️ Rate limited - disabling price grid for 60 seconds');
                setRateLimited(true);
                setRateLimitEnd(Date.now() + 60000); // 60 seconds
              }
              
              return { date, price: null, currency: 'USD' };
            })
        );
      }
      
      const results = await Promise.all(pricePromises);
      setPriceData(results);
      
      // Find min and max prices for color coding
      const prices = results.filter(r => r.price !== null).map(r => parseFloat(r.price));
      if (prices.length > 0) {
        setCheapestPrice(Math.min(...prices));
        setMostExpensivePrice(Math.max(...prices));
      }
      
    } catch (error) {
      console.error('Error fetching price grid:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriceColor = (price) => {
    if (!price || !cheapestPrice || !mostExpensivePrice) return '#f5f5f5';
    
    const priceFloat = parseFloat(price);
    const range = mostExpensivePrice - cheapestPrice;
    const position = (priceFloat - cheapestPrice) / range;
    
    // Green for cheap, yellow for medium, red for expensive
    if (position < 0.33) return '#d4edda'; // Light green
    if (position < 0.66) return '#fff3cd'; // Light yellow
    return '#f8d7da'; // Light red
  };

  const handleDateClick = (priceItem) => {
    if (!priceItem.price) return;
    
    const clickedDate = format(priceItem.date, 'yyyy-MM-dd');
    
    if (tripType !== 'round_trip') {
      // One-way trip - just update departure
      onDateSelect(clickedDate, null);
      return;
    }
    
    // Check if we're in the Duffel two-step selection flow
    if (selectionStep === 2) {
      // User is selecting return flight - clicking calendar should update RETURN date only
      console.log('📅 Return date selected from calendar:', clickedDate);
      onDateSelect(departDate, clickedDate); // Keep current departure, update return
      return;
    }
    
    // We're in step 1 - clicking a date updates departure, keeps original return
    console.log('📅 Departure date selected from calendar:', clickedDate);
    onDateSelect(clickedDate, returnDate); // Update departure, keep current return
  };

  if (!origin || !destination || !departDate) {
    return null;
  }

  const getRateLimitMessage = () => {
    if (!rateLimited || !rateLimitEnd) return '';
    const secondsLeft = Math.ceil((rateLimitEnd - Date.now()) / 1000);
    return `Rate limited. Retrying in ${secondsLeft}s...`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Price Calendar {rateLimited && '(Paused)'}
          </Typography>
          {tripType === 'round_trip' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectionStep === 2 ? (
                'Click a date to change the return flight date'
              ) : (
                'Click a date to change the departure flight date'
              )}
            </Typography>
          )}
        </Box>
        {rateLimited ? (
          <Chip 
            label={getRateLimitMessage()} 
            color="warning" 
            size="small" 
          />
        ) : cheapestPrice && (
          <Chip 
            label={`Cheapest: $${cheapestPrice.toFixed(2)}`} 
            color="success" 
            size="small" 
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => setWeekOffset(weekOffset - 1)}
          disabled={loading}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          <ChevronLeftIcon />
        </IconButton>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1, py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box 
            sx={{ 
              flex: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                }
              }
            }}
          >
            <Box 
              sx={{ 
                display: 'flex',
                gap: 1,
                minWidth: 'min-content',
                pb: 1,
              }}
            >
              {priceData.map((item, index) => {
                const isCurrentDepart = isSameDay(item.date, parseISO(departDate));
                const isCurrentReturn = returnDate && isSameDay(item.date, parseISO(returnDate));
                const isCheapest = item.price && parseFloat(item.price) === cheapestPrice;
                
                const isSelected = isCurrentDepart || isCurrentReturn;
                const borderColor = isSelected ? '#667eea' : '#e0e0e0';
                const borderWidth = isSelected ? '2px' : '1px';
                
                return (
                  <Paper
                    key={index}
                    elevation={isSelected ? 4 : 1}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: item.price ? 'pointer' : 'default',
                      bgcolor: getPriceColor(item.price),
                      border: `${borderWidth} solid ${borderColor}`,
                      transition: 'all 0.2s',
                      minWidth: { xs: 120, sm: 140 },
                      flexShrink: 0,
                      '&:hover': item.price ? {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      } : {}
                    }}
                    onClick={() => handleDateClick(item)}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                      {format(item.date, 'EEE')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      {format(item.date, 'MMM d')}
                    </Typography>
                    {item.price ? (
                      <>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                          ${parseFloat(item.price).toFixed(0)}
                        </Typography>
                        {isCheapest && (
                          <Chip 
                            label="Best" 
                            size="small" 
                            color="success" 
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} 
                          />
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}

        <IconButton 
          onClick={() => setWeekOffset(weekOffset + 1)}
          disabled={loading}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        Click on a date to see flights for that day. Use arrows to view different weeks.
      </Typography>
    </Paper>
  );
}

export default FlightPriceGrid;

